import React, { useState, useEffect } from 'react';
import { Menu, X, Calendar } from 'lucide-react';
import { Button } from '../ui/button';

// PixZent Logo - Image based with standard size
const PixZentLogo = ({ className = "h-12 w-auto" }) => (
  <img 
    src="/pixzent-logo.png" 
    alt="PixZent" 
    className={className}
    style={{ objectFit: 'contain', maxHeight: '48px' }}
  />
);

const Header = ({ onBookCall, onGetAudit }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center group">
            <PixZentLogo className="h-12 w-auto transition-transform duration-300 group-hover:scale-105" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('features')}
              className={`text-sm font-medium transition-colors hover:text-violet-600 ${
                isScrolled ? 'text-slate-700' : 'text-slate-700'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('process')}
              className={`text-sm font-medium transition-colors hover:text-violet-600 ${
                isScrolled ? 'text-slate-700' : 'text-slate-700'
              }`}
            >
              Process
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className={`text-sm font-medium transition-colors hover:text-violet-600 ${
                isScrolled ? 'text-slate-700' : 'text-slate-700'
              }`}
            >
              Results
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className={`text-sm font-medium transition-colors hover:text-violet-600 ${
                isScrolled ? 'text-slate-700' : 'text-slate-700'
              }`}
            >
              FAQ
            </button>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onGetAudit}
              className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-300 transition-all duration-300"
            >
              Free Audit
            </Button>
            <Button
              onClick={onBookCall}
              className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 hover:from-blue-700 hover:via-violet-700 hover:to-pink-600 text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 gap-2"
            >
              <Calendar className="w-4 h-4" />
              Book a Call
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-lg transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="px-4 py-6 space-y-4">
          <button
            onClick={() => scrollToSection('features')}
            className="block w-full text-left py-2 text-slate-700 hover:text-violet-600 font-medium"
          >
            Services
          </button>
          <button
            onClick={() => scrollToSection('process')}
            className="block w-full text-left py-2 text-slate-700 hover:text-violet-600 font-medium"
          >
            Process
          </button>
          <button
            onClick={() => scrollToSection('testimonials')}
            className="block w-full text-left py-2 text-slate-700 hover:text-violet-600 font-medium"
          >
            Results
          </button>
          <button
            onClick={() => scrollToSection('faq')}
            className="block w-full text-left py-2 text-slate-700 hover:text-violet-600 font-medium"
          >
            FAQ
          </button>
          <div className="pt-4 space-y-3">
            <Button
              variant="outline"
              onClick={() => { onGetAudit(); setIsMobileMenuOpen(false); }}
              className="w-full border-violet-200 text-violet-700"
            >
              Free Audit
            </Button>
            <Button
              onClick={() => { onBookCall(); setIsMobileMenuOpen(false); }}
              className="w-full bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 text-white gap-2"
            >
              <Calendar className="w-4 h-4" />
              Book a Call
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export { PixZentLogo };
export default Header;
